import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import type { BlacksmithConfig, PlayerGemStack } from "../api/blacksmith";
import { AssetIcon } from "./AssetIcon";
import {
  formatGemLabel,
  gemStatKey,
  gemTypeColor,
  gemTypeSurface,
} from "./GemPickerModal";
import { getGemIconPath, GEM_ICON_FALLBACK } from "../utils/assets";
import { ModalBackdrop } from "./ModalBackdrop";

const CRAFTABLE_TYPES = ["PATK", "PDEF", "MATK", "MDEF", "CRIT", "HP"] as const;
const COLS = 4;
const BOARD_STORAGE_PREFIX = "meltown.gem-craft-board.v1";

type CraftableType = (typeof CRAFTABLE_TYPES)[number];
type SavedCell = { gem_type: string; level: number } | null;

function isCraftableType(gemType: string): gemType is CraftableType {
  return (CRAFTABLE_TYPES as readonly string[]).includes(gemType);
}

function boardStorageKey(userId?: number): string {
  return userId ? `${BOARD_STORAGE_PREFIX}.${userId}` : `${BOARD_STORAGE_PREFIX}.guest`;
}

function loadSavedLayout(userId?: number): SavedCell[] | null {
  try {
    const raw = localStorage.getItem(boardStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed.map((cell) => {
      if (cell == null) return null;
      if (typeof cell !== "object") return null;
      const gem_type = (cell as { gem_type?: unknown }).gem_type;
      const level = (cell as { level?: unknown }).level;
      if (typeof gem_type !== "string" || typeof level !== "number") return null;
      if (!isCraftableType(gem_type)) return null;
      return { gem_type, level };
    });
  } catch {
    return null;
  }
}

function saveBoardLayout(userId: number | undefined, board: (GemInstance | null)[]): void {
  try {
    const payload: SavedCell[] = board.map((gem) =>
      gem ? { gem_type: gem.gem_type, level: gem.level } : null,
    );
    localStorage.setItem(boardStorageKey(userId), JSON.stringify(payload));
  } catch {
    // ignore quota / private mode
  }
}

function padBoard(board: (GemInstance | null)[]): (GemInstance | null)[] {
  const gemCount = board.filter(Boolean).length;
  const minLen = Math.max(board.length, Math.ceil(Math.max(gemCount, 1) / COLS) * COLS);
  const next = [...board];
  while (next.length < minLen) next.push(null);
  return next;
}

function reconcileBoard(
  saved: SavedCell[] | null,
  instances: GemInstance[],
): (GemInstance | null)[] {
  const pool = new Map<string, GemInstance[]>();
  for (const inst of instances) {
    const key = `${inst.gem_type}:${inst.level}`;
    const bucket = pool.get(key);
    if (bucket) bucket.push(inst);
    else pool.set(key, [inst]);
  }

  const result: (GemInstance | null)[] = [];
  if (saved && saved.length > 0) {
    for (const cell of saved) {
      if (!cell) {
        result.push(null);
        continue;
      }
      const key = `${cell.gem_type}:${cell.level}`;
      const bucket = pool.get(key);
      if (bucket && bucket.length > 0) {
        result.push(bucket.shift()!);
      } else {
        result.push(null);
      }
    }
  }

  const leftovers: GemInstance[] = [];
  for (const bucket of pool.values()) leftovers.push(...bucket);

  let leftIdx = 0;
  for (let i = 0; i < result.length && leftIdx < leftovers.length; i++) {
    if (!result[i]) {
      result[i] = leftovers[leftIdx++];
    }
  }
  while (leftIdx < leftovers.length) {
    result.push(leftovers[leftIdx++]);
  }

  if (result.length === 0 && instances.length > 0) {
    return padBoard([...instances]);
  }

  return padBoard(result);
}

interface GemInstance {
  uid: string;
  gem_type: string;
  level: number;
}

interface MergeRun {
  indices: number[];
  gemType: string;
  level: number;
}

/** All tetromino shapes (4 orthogonally connected cells), normalized to top-left anchor. */
const MERGE_SHAPES: readonly [number, number][][] = [
  [[0, 0], [0, 1], [0, 2], [0, 3]],
  [[0, 0], [1, 0], [2, 0], [3, 0]],
  [[0, 0], [0, 1], [1, 0], [1, 1]],
  [[0, 0], [0, 1], [0, 2], [1, 1]],
  [[0, 0], [1, 0], [2, 0], [1, 1]],
  [[0, 1], [1, 0], [1, 1], [1, 2]],
  [[0, 1], [1, 0], [1, 1], [2, 1]],
  [[0, 0], [1, 0], [2, 0], [2, 1]],
  [[0, 0], [0, 1], [0, 2], [1, 0]],
  [[0, 0], [0, 1], [1, 1], [2, 1]],
  [[0, 1], [1, 1], [2, 0], [2, 1]],
  [[0, 1], [1, 1], [2, 1], [2, 0]],
  [[0, 0], [1, 0], [0, 1], [0, 2]],
  [[0, 0], [1, 0], [2, 0], [0, 1]],
  [[0, 0], [0, 1], [1, 1], [2, 1]],
  [[0, 1], [0, 2], [1, 0], [1, 1]],
  [[0, 0], [1, 0], [1, 1], [2, 1]],
  [[0, 0], [0, 1], [1, 1], [1, 2]],
  [[0, 1], [1, 0], [1, 1], [2, 0]],
];

function boardIndex(row: number, col: number, boardLength: number): number | null {
  if (row < 0 || col < 0 || col >= COLS) return null;
  const idx = row * COLS + col;
  if (idx >= boardLength) return null;
  return idx;
}

function instancesToBoard(instances: GemInstance[]): (GemInstance | null)[] {
  const rows = Math.max(1, Math.ceil(instances.length / COLS));
  const board: (GemInstance | null)[] = [...instances];
  while (board.length < rows * COLS) board.push(null);
  return board;
}

function findMergeRun(board: (GemInstance | null)[]): MergeRun | null {
  const rows = Math.ceil(board.length / COLS);
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < COLS; col++) {
      for (const shape of MERGE_SHAPES) {
        const indices: number[] = [];
        let valid = true;
        for (const [dr, dc] of shape) {
          const idx = boardIndex(row + dr, col + dc, board.length);
          if (idx === null) {
            valid = false;
            break;
          }
          indices.push(idx);
        }
        if (!valid) continue;

        const gems = indices.map((i) => board[i]);
        if (gems.some((g) => !g)) continue;
        const first = gems[0]!;
        if (first.gem_type === "LUCK") continue;
        if (gems.every((g) => g!.gem_type === first.gem_type && g!.level === first.level)) {
          return { indices, gemType: first.gem_type, level: first.level };
        }
      }
    }
  }
  return null;
}

/** Layout where no 4 matching gems form a mergeable cluster (row, square, L, T, …). */
function safeLayout(instances: GemInstance[]): GemInstance[] {
  if (instances.length === 0) return [];

  for (let attempt = 0; attempt < 300; attempt++) {
    const candidate = shuffle([...instances]);
    if (!findMergeRun(instancesToBoard(candidate))) return candidate;
  }

  const result: GemInstance[] = [];
  for (const gem of shuffle([...instances])) {
    let placed = false;
    for (let pos = 0; pos <= result.length; pos++) {
      const testBoard = instancesToBoard([...result.slice(0, pos), gem, ...result.slice(pos)]);
      if (!findMergeRun(testBoard)) {
        result.splice(pos, 0, gem);
        placed = true;
        break;
      }
    }
    if (!placed) result.push(gem);
  }
  return result;
}

interface GemCraftPanelProps {
  gems: PlayerGemStack[];
  config: BlacksmithConfig | null;
  gold: number;
  crypto: number;
  heroClass?: string;
  userId?: number;
  actionLoading: boolean;
  autoCraftBasicUnlocked?: boolean;
  autoCraftFullUnlocked?: boolean;
  onNeedPremium?: () => void;
  onSynthesize: (gemType: string, fromLevel: number, luckLevel: number) => Promise<void>;
  onAutoCraft: () => Promise<void>;
  onAutoCraftFull: (confirmWithoutLuck?: boolean) => Promise<boolean>;
  onPreviewAutoCraftFull: () => Promise<import("../api/blacksmith").AutoCraftFullPreview | null>;
  onSellGem: (gemType: string, level: number, quantity: number) => Promise<void>;
  onExchangeGem: (fromGemType: string, toGemType: string, level: number) => Promise<void>;
}

function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function buildInstances(gems: PlayerGemStack[]): GemInstance[] {
  const rows: GemInstance[] = [];
  let seq = 0;
  for (const g of gems) {
    if (g.quantity <= 0 || !isCraftableType(g.gem_type)) continue;
    for (let i = 0; i < g.quantity; i++) {
      rows.push({ uid: `${g.gem_type}-${g.level}-${seq++}`, gem_type: g.gem_type, level: g.level });
    }
  }
  return rows;
}

function buildBoard(instances: GemInstance[], userId?: number): (GemInstance | null)[] {
  const saved = loadSavedLayout(userId);
  const board = saved ? reconcileBoard(saved, instances) : padBoard(safeLayout(instances));
  saveBoardLayout(userId, board);
  return board;
}

function synthesisInfo(config: BlacksmithConfig | null, level: number) {
  const row = config?.gem_craft?.synthesis_by_level?.[String(level)];
  if (row) return row;
  const goldCost = Math.round(5000 * Math.pow(2.5, level - 1));
  const baseRate = Math.max(10, 100 - (level - 1) * 8);
  return { gold_cost: goldCost, success_rate: baseRate, to_level: level + 1 };
}

export function GemCraftPanel({
  gems,
  config,
  gold,
  crypto,
  heroClass,
  userId,
  actionLoading,
  autoCraftBasicUnlocked = false,
  autoCraftFullUnlocked = false,
  onNeedPremium,
  onSynthesize,
  onAutoCraft,
  onAutoCraftFull,
  onPreviewAutoCraftFull,
  onSellGem,
  onExchangeGem,
}: GemCraftPanelProps) {
  const [board, setBoard] = useState<(GemInstance | null)[]>([]);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [dragFromIdx, setDragFromIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [previewType, setPreviewType] = useState("PATK");
  const [previewLevel, setPreviewLevel] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [luckModal, setLuckModal] = useState<{ gemType: string; fromLevel: number; rate: number; goldCost: number } | null>(null);
  const [detailGem, setDetailGem] = useState<GemInstance | null>(null);
  const [fullCraftPreview, setFullCraftPreview] = useState<import("../api/blacksmith").AutoCraftFullPreview | null>(null);
  const [sellQty, setSellQty] = useState(1);
  const [exchangeFrom, setExchangeFrom] = useState<CraftableType>("CRIT");
  const [exchangeTo, setExchangeTo] = useState<CraftableType>("HP");
  const [exchangeLevel, setExchangeLevel] = useState(1);
  const mergeLock = useRef(false);
  const boardRef = useRef(board);
  boardRef.current = board;
  const selectedIdxRef = useRef(selectedIdx);
  selectedIdxRef.current = selectedIdx;
  const endGestureRef = useRef<(() => void) | null>(null);
  const dragState = useRef<{
    fromIdx: number;
    startX: number;
    startY: number;
    active: boolean;
    pointerId: number;
  } | null>(null);

  const cleanupGesture = useCallback(() => {
    endGestureRef.current?.();
    endGestureRef.current = null;
  }, []);

  useEffect(() => () => cleanupGesture(), [cleanupGesture]);

  const cellIndexFromPoint = (x: number, y: number): number | null => {
    const el = document.elementFromPoint(x, y)?.closest("[data-gem-idx]");
    if (!el) return null;
    const idx = Number(el.getAttribute("data-gem-idx"));
    return Number.isFinite(idx) ? idx : null;
  };

  const maxLevel = config?.gem_craft?.max_level ?? 12;
  const autoMax = config?.gem_craft?.auto_craft_max_output_level ?? 3;

  const craftableGems = useMemo(
    () => gems.filter((g) => isCraftableType(g.gem_type)),
    [gems],
  );

  const gemsKey = useMemo(
    () => craftableGems.map((g) => `${g.gem_type}:${g.level}:${g.quantity}`).join("|"),
    [craftableGems],
  );

  const persistBoard = useCallback(
    (next: (GemInstance | null)[]) => {
      saveBoardLayout(userId, next);
    },
    [userId],
  );

  useEffect(() => {
    const next = buildBoard(buildInstances(craftableGems), userId);
    setBoard(next);
    setSelectedIdx(null);
    setDragFromIdx(null);
    setDragOverIdx(null);
    mergeLock.current = false;
    setLuckModal(null);
  }, [gemsKey, userId]);

  const ownedTypes = useMemo(
    () => new Set(craftableGems.map((g) => `${g.gem_type}-${g.level}`)),
    [craftableGems],
  );
  const luckStacks = gems.filter((g) => g.gem_type === "LUCK" && g.quantity > 0);

  const tryMergeAfterMove = useCallback(
    (nextBoard: (GemInstance | null)[]) => {
      if (mergeLock.current || actionLoading) return;
      const run = findMergeRun(nextBoard);
      if (!run) return;
      const synth = synthesisInfo(config, run.level);
      if (gold < synth.gold_cost) return;
      mergeLock.current = true;
      if (synth.success_rate >= 100) {
        void onSynthesize(run.gemType, run.level, 0);
        return;
      }
      setLuckModal({
        gemType: run.gemType,
        fromLevel: run.level,
        rate: synth.success_rate,
        goldCost: synth.gold_cost,
      });
    },
    [actionLoading, config, gold, onSynthesize],
  );

  const swapCells = useCallback(
    (from: number, to: number) => {
      if (from === to) return;
      setBoard((prev) => {
        const next = padBoard([...prev]);
        [next[from], next[to]] = [next[to], next[from]];
        persistBoard(next);
        setTimeout(() => tryMergeAfterMove(next), 0);
        return next;
      });
      setSelectedIdx(null);
    },
    [persistBoard, tryMergeAfterMove],
  );

  const handleCellTap = useCallback(
    (idx: number) => {
      if (actionLoading) return;
      const gem = boardRef.current[idx];
      const sel = selectedIdxRef.current;
      if (sel === null) {
        if (gem) setSelectedIdx(idx);
        return;
      }
      if (sel === idx) {
        if (gem) {
          setDetailGem(gem);
          setSellQty(1);
        }
        setSelectedIdx(null);
        return;
      }
      swapCells(sel, idx);
    },
    [actionLoading, swapCells],
  );

  const handleCellTapRef = useRef(handleCellTap);
  handleCellTapRef.current = handleCellTap;

  const beginCellGesture = useCallback(
    (idx: number, e: React.PointerEvent) => {
      if (actionLoading) return;
      const hasGem = !!boardRef.current[idx];
      if (!hasGem && selectedIdxRef.current === null) return;

      e.preventDefault();
      cleanupGesture();

      const pointerId = e.pointerId;
      dragState.current = {
        fromIdx: idx,
        startX: e.clientX,
        startY: e.clientY,
        active: false,
        pointerId,
      };

      const onMove = (ev: PointerEvent) => {
        const ds = dragState.current;
        if (!ds || ev.pointerId !== pointerId) return;

        if (!ds.active) {
          const moved = Math.hypot(ev.clientX - ds.startX, ev.clientY - ds.startY);
          if (moved >= 10) {
            if (!boardRef.current[ds.fromIdx]) return;
            ds.active = true;
            setDragFromIdx(ds.fromIdx);
            setSelectedIdx(null);
          }
        }
        if (ds.active) {
          setDragOverIdx(cellIndexFromPoint(ev.clientX, ev.clientY));
        }
      };

      const onUp = (ev: PointerEvent) => {
        const ds = dragState.current;
        if (!ds || ev.pointerId !== pointerId) return;

        cleanupGesture();
        const fromIdx = ds.fromIdx;
        const wasDrag = ds.active;
        dragState.current = null;
        setDragFromIdx(null);

        const targetIdx = cellIndexFromPoint(ev.clientX, ev.clientY);
        setDragOverIdx(null);

        if (wasDrag) {
          if (targetIdx !== null && targetIdx !== fromIdx) {
            swapCells(fromIdx, targetIdx);
          }
        } else {
          handleCellTapRef.current(targetIdx ?? idx);
        }
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
      endGestureRef.current = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };
    },
    [actionLoading, cleanupGesture, swapCells],
  );

  const sellPrice =
    detailGem != null
      ? Number(config?.gem_craft?.sell_gold_by_level?.[String(detailGem.level)] ?? 120 * Math.pow(1.75, detailGem.level - 1))
      : 0;
  const detailStackQty = detailGem
    ? craftableGems.find((g) => g.gem_type === detailGem.gem_type && g.level === detailGem.level)?.quantity ?? 0
    : 0;

  const gemCount = board.filter(Boolean).length;
  const mergeHint = findMergeRun(board);

  return (
    <div className="space-y-4">
      <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <p className="text-gray-300 text-sm">
          Drag gems to rearrange — line up 4 matching gems in a row, square, L, or T shape to combine.
          You can also tap one gem, then another to swap.
        </p>
        <p className="text-xs text-gray-500">
          Craftable: {CRAFTABLE_TYPES.map((t) => formatGemLabel(t, heroClass)).join(", ")} (Lv.1–{maxLevel})
        </p>
        <button
          type="button"
          disabled={actionLoading}
          onClick={() => {
            if (!autoCraftBasicUnlocked) {
              onNeedPremium?.();
              return;
            }
            void onAutoCraft();
          }}
          className={clsx(
            "w-full py-2.5 rounded-xl border text-sm font-bold disabled:opacity-40",
            autoCraftBasicUnlocked
              ? "border-violet-500/50 bg-violet-950/40 text-violet-200"
              : "border-gray-600/50 bg-gray-900/40 text-gray-400",
          )}
        >
          ⚡ Auto-craft up to Lv.{autoMax} (no luck stones)
          {!autoCraftBasicUnlocked && (
            <span className="block text-[10px] font-normal text-amber-400/90 mt-0.5">Requires active VIP</span>
          )}
        </button>
        <button
          type="button"
          disabled={actionLoading}
          onClick={() => {
            if (!autoCraftFullUnlocked) {
              onNeedPremium?.();
              return;
            }
            void (async () => {
              const preview = await onPreviewAutoCraftFull();
              if (!preview) return;
              if (preview.attempts <= 0) return;
              if (preview.needs_confirm) {
                setFullCraftPreview(preview);
                return;
              }
              await onAutoCraftFull(false);
            })();
          }}
          className={clsx(
            "w-full py-2.5 rounded-xl border text-sm font-bold disabled:opacity-40",
            autoCraftFullUnlocked
              ? "border-amber-500/50 bg-amber-950/30 text-amber-100"
              : "border-gray-600/50 bg-gray-900/40 text-gray-400",
          )}
        >
          ⚡ Auto-craft all levels (uses luck stones)
          {!autoCraftFullUnlocked && (
            <span className="block text-[10px] font-normal text-amber-400/90 mt-0.5">
              Requires VIP Lv.4+ or VIP with Pro
            </span>
          )}
        </button>
      </div>

      <GemExchangePanel
        gems={craftableGems}
        config={config}
        heroClass={heroClass}
        gold={gold}
        crypto={crypto}
        fromType={exchangeFrom}
        toType={exchangeTo}
        level={exchangeLevel}
        loading={actionLoading}
        onFromChange={setExchangeFrom}
        onToChange={setExchangeTo}
        onLevelChange={setExchangeLevel}
        onExchange={() => void onExchangeGem(exchangeFrom, exchangeTo, exchangeLevel)}
      />

      <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-3 space-y-2">
        <div className="text-xs text-gray-400 font-bold uppercase tracking-wide">Gem catalog</div>
        <div className="flex flex-wrap gap-1.5">
          {CRAFTABLE_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setPreviewType(t);
                setPreviewLevel(1);
                setShowPreview(true);
              }}
              className={clsx(
                "px-2 py-1.5 rounded-lg text-xs font-bold border flex items-center gap-1",
                gemTypeSurface(t, heroClass),
                gemTypeColor(t, heroClass),
              )}
            >
              <AssetIcon
                src={getGemIconPath(t, 1)}
                fallback={GEM_ICON_FALLBACK}
                alt={t}
                className="w-5 h-5 object-contain"
                emojiClassName="text-sm"
              />
              {formatGemLabel(t, heroClass)}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-gray-500">Tap a type to preview any level, even if you don't own it yet.</p>
      </div>

      <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-400 font-bold uppercase tracking-wide">Your gems</div>
          <div className="text-[10px] text-gray-500">{gemCount} gems</div>
        </div>

        {luckStacks.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] text-gray-500 uppercase tracking-wide">Luck stones</span>
            {luckStacks.map((l) => (
              <span
                key={`luck-${l.level}`}
                className="text-[10px] px-2 py-0.5 rounded-full border border-violet-600/50 bg-violet-950/30 text-violet-200"
              >
                Lv.{l.level} ×{l.quantity}
              </span>
            ))}
            <span className="text-[10px] text-gray-500">— used when crafting, not on the board</span>
          </div>
        )}

        {gemCount === 0 && (
          <p className="text-gray-500 text-sm text-center py-4">No gems. Clear catacombs floors to earn gems.</p>
        )}

        <div className="grid grid-cols-4 gap-2">
          {board.map((gem, idx) => (
            <BoardCell
              key={`cell-${idx}-${gem?.uid ?? "empty"}`}
              idx={idx}
              gem={gem}
              heroClass={heroClass}
              selected={selectedIdx === idx}
              dragging={dragFromIdx === idx}
              dropTarget={dragOverIdx === idx && dragFromIdx !== idx}
              mergeHighlight={mergeHint != null && mergeHint.indices.includes(idx)}
              swapTarget={selectedIdx !== null && selectedIdx !== idx}
              onPointerDown={(e) => beginCellGesture(idx, e)}
            />
          ))}
        </div>

        {mergeHint && actionLoading && (
          <div className="text-xs text-center text-emerald-400">
            Forging 4× {formatGemLabel(mergeHint.gemType, heroClass)} Lv.{mergeHint.level}…
          </div>
        )}

        {mergeHint && !actionLoading && gold < synthesisInfo(config, mergeHint.level).gold_cost && (
          <div className="text-xs text-center text-rose-400">Not enough gold to combine</div>
        )}

        <p className="text-[10px] text-gray-500 text-center">
          Drag a gem onto another slot · or tap two gems to swap · tap selected gem again for details
        </p>
      </div>

      {showPreview && (
        <PreviewModal
          gemType={previewType}
          level={previewLevel}
          maxLevel={maxLevel}
          heroClass={heroClass}
          config={config}
          owned={ownedTypes.has(`${previewType}-${previewLevel}`)}
          onLevelChange={setPreviewLevel}
          onClose={() => setShowPreview(false)}
        />
      )}

      {luckModal && (
        <LuckModal
          gemType={luckModal.gemType}
          fromLevel={luckModal.fromLevel}
          rate={luckModal.rate}
          goldCost={luckModal.goldCost}
          luckStacks={luckStacks}
          luckBonusPerLevel={config?.gem_craft?.luck_bonus_per_level ?? 0.1}
          loading={actionLoading}
          onCancel={() => {
            mergeLock.current = false;
            setLuckModal(null);
          }}
          onConfirm={(luckLevel) => {
            void onSynthesize(luckModal.gemType, luckModal.fromLevel, luckLevel).then(() => {
              setLuckModal(null);
            });
          }}
        />
      )}

      {fullCraftPreview && (
        <ModalBackdrop onClose={() => setFullCraftPreview(null)}>
          <div className="w-full max-w-sm bg-kingdom-purple border border-amber-700/50 rounded-2xl p-4 space-y-3">
            <h3 className="font-bold text-amber-100">Full auto-craft — risk</h3>
            <p className="text-sm text-gray-300">{fullCraftPreview.message}</p>
            <ul className="text-xs text-gray-400 space-y-1">
              <li>Estimated crafts: {fullCraftPreview.attempts}</li>
              <li>
                Gold: {fullCraftPreview.gold_needed.toLocaleString()} / owned{" "}
                {fullCraftPreview.gold_owned.toLocaleString()}
              </li>
              <li className="text-violet-200/90">
                Luck stones (100% success): need{" "}
                {fullCraftPreview.luck_stones_required ?? fullCraftPreview.luck_stones_to_spend} · you
                have {fullCraftPreview.luck_owned} · will use{" "}
                {fullCraftPreview.luck_stones_to_spend}
                {(fullCraftPreview.luck_stones_shortfall ?? 0) > 0 && (
                  <span className="text-rose-300">
                    {" "}
                    · buy ~{fullCraftPreview.luck_stones_shortfall} more
                  </span>
                )}
              </li>
            </ul>
            {(fullCraftPreview.luck_stones_shortfall ?? 0) > 0 ? (
              <p className="text-xs text-rose-300">
                Missing {fullCraftPreview.luck_stones_shortfall} luck stone(s) for a safe run. Some
                crafts may fail (lose 1 gem + gold). Buy luck stones in the shop, or continue at your
                risk.
              </p>
            ) : (
              <p className="text-xs text-gray-500">
                Enough luck stones for guaranteed success on all planned crafts.
              </p>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                className="flex-1 py-2 rounded-xl border border-kingdom-mid text-gray-300 text-sm"
                onClick={() => setFullCraftPreview(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionLoading}
                className="flex-1 py-2 rounded-xl bg-amber-800 text-white text-sm font-bold disabled:opacity-40"
                onClick={() => {
                  void onAutoCraftFull(true).then((ok) => {
                    if (ok) setFullCraftPreview(null);
                  });
                }}
              >
                Craft anyway
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {detailGem && (
        <GemQuickModal
          gem={detailGem}
          heroClass={heroClass}
          maxSellQty={detailStackQty}
          sellQty={sellQty}
          sellPrice={sellPrice}
          loading={actionLoading}
          onSellQtyChange={setSellQty}
          onClose={() => setDetailGem(null)}
          onSell={() =>
            void onSellGem(detailGem.gem_type, detailGem.level, sellQty).then(() => {
              setDetailGem(null);
              setSelectedIdx(null);
            })
          }
        />
      )}
    </div>
  );
}

function BoardCell({
  idx,
  gem,
  heroClass,
  selected,
  dragging,
  dropTarget,
  mergeHighlight,
  swapTarget,
  onPointerDown,
}: {
  idx: number;
  gem: GemInstance | null;
  heroClass?: string;
  selected: boolean;
  dragging: boolean;
  dropTarget: boolean;
  mergeHighlight: boolean;
  swapTarget: boolean;
  onPointerDown: (e: React.PointerEvent) => void;
}) {
  if (!gem) {
    return (
      <div
        data-gem-idx={idx}
        onPointerDown={swapTarget ? onPointerDown : undefined}
        className={clsx(
          "aspect-[4/5] rounded-lg border-2 border-dashed transition-colors touch-none select-none",
          dropTarget || swapTarget ? "border-kingdom-glow bg-kingdom-glow/10" : "border-gray-800 bg-kingdom-dark/30",
          swapTarget && "cursor-pointer",
        )}
      />
    );
  }

  const label = formatGemLabel(gem.gem_type, heroClass);

  return (
    <button
      type="button"
      data-gem-idx={idx}
      onPointerDown={onPointerDown}
      className={clsx(
        "aspect-[4/5] rounded-lg border flex flex-col items-center justify-center p-1 touch-none select-none transition-all",
        gemTypeSurface(gem.gem_type, heroClass),
        dragging && "opacity-60 scale-95 z-10",
        selected && "ring-2 ring-kingdom-glow border-kingdom-glow scale-105",
        dropTarget && !selected && "ring-2 ring-kingdom-glow/60 border-kingdom-glow/60",
        mergeHighlight && !selected && !dropTarget && "ring-2 ring-emerald-500/70 border-emerald-500/70",
        !selected && !dropTarget && !mergeHighlight && !dragging && "border-kingdom-mid active:scale-95",
      )}
    >
      <AssetIcon
        src={getGemIconPath(gem.gem_type, gem.level)}
        fallback={GEM_ICON_FALLBACK}
        alt={label}
        className="w-8 h-8 object-contain pointer-events-none"
        emojiClassName="text-lg"
      />
      <span className={clsx("font-bold truncate w-full text-center leading-tight text-[9px]", gemTypeColor(gem.gem_type, heroClass))}>
        {label}
      </span>
      <span className="text-[9px] text-gray-400 font-bold">Lv.{gem.level}</span>
    </button>
  );
}

function PreviewModal({
  gemType,
  level,
  maxLevel,
  heroClass,
  config,
  owned,
  onLevelChange,
  onClose,
}: {
  gemType: string;
  level: number;
  maxLevel: number;
  heroClass?: string;
  config: BlacksmithConfig | null;
  owned: boolean;
  onLevelChange: (lv: number) => void;
  onClose: () => void;
}) {
  const stats = config?.gem_craft?.stats_by_level?.[String(level)] ?? {};
  const statKey = gemStatKey(gemType, heroClass);
  const statVal = stats[statKey] ?? stats[gemType];
  const synth = level < maxLevel ? synthesisInfo(config, level) : null;
  const sellGold = config?.gem_craft?.sell_gold_by_level?.[String(level)];

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="w-full max-w-sm bg-kingdom-purple border border-kingdom-light rounded-2xl p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className={clsx("w-16 h-16 rounded-xl border-2 flex items-center justify-center shrink-0", gemTypeSurface(gemType, heroClass))}>
            <AssetIcon
              src={getGemIconPath(gemType, level)}
              fallback={GEM_ICON_FALLBACK}
              alt={gemType}
              className="w-12 h-12 object-contain"
              emojiClassName="text-3xl"
            />
          </div>
          <div className="flex-1">
            <div className={clsx("font-bold text-lg", gemTypeColor(gemType, heroClass))}>
              {formatGemLabel(gemType, heroClass)} Lv.{level}
            </div>
            {!owned && <div className="text-xs text-gray-500">Preview — gem not owned yet</div>}
            {statVal != null && <div className="text-sm text-emerald-400 mt-1">+{statVal}</div>}
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        <input
          type="range"
          min={1}
          max={maxLevel}
          value={level}
          onChange={(e) => onLevelChange(Number(e.target.value))}
          className="w-full"
        />
        {synth && (
          <div className="text-xs text-gray-400 space-y-0.5">
            <div>Craft Lv.{level}→{synth.to_level}: {synth.gold_cost.toLocaleString()} gold, {Math.round(synth.success_rate)}% success</div>
          </div>
        )}
        {sellGold != null && <div className="text-xs text-amber-200/80">Sell value: {Number(sellGold).toLocaleString()} gold</div>}
      </div>
    </ModalBackdrop>
  );
}

function LuckModal({
  gemType,
  fromLevel,
  rate,
  goldCost,
  luckStacks,
  luckBonusPerLevel,
  loading,
  onCancel,
  onConfirm,
}: {
  gemType: string;
  fromLevel: number;
  rate: number;
  goldCost: number;
  luckStacks: PlayerGemStack[];
  luckBonusPerLevel: number;
  loading: boolean;
  onCancel: () => void;
  onConfirm: (luckLevel: number) => void;
}) {
  const [luckLevel, setLuckLevel] = useState(0);
  const bonus = luckLevel * luckBonusPerLevel * 100;
  const total = Math.min(100, rate + bonus);

  return (
    <ModalBackdrop onClose={onCancel}>
      <div className="w-full max-w-sm bg-kingdom-purple border border-kingdom-light rounded-2xl p-4 space-y-3">
        <h3 className="font-bold text-white">{Math.round(rate)}% success rate</h3>
        <p className="text-sm text-gray-400">
          On failure you lose 1 {gemType} Lv.{fromLevel} gem and {goldCost.toLocaleString()} gold. Use a luck stone?
        </p>
        <select
          value={luckLevel}
          onChange={(e) => setLuckLevel(Number(e.target.value))}
          className="w-full bg-kingdom-dark border border-kingdom-mid rounded-lg px-3 py-2 text-sm text-white"
        >
          <option value={0}>No luck stone ({Math.round(rate)}%)</option>
          {luckStacks.map((l) => (
            <option key={`luck-${l.level}`} value={l.level}>
              LUCK Lv.{l.level} (+{Math.round(l.level * luckBonusPerLevel * 100)}%)
            </option>
          ))}
        </select>
        <div className="text-xs text-emerald-400">Final success rate: {Math.round(total)}%</div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="flex-1 py-2.5 rounded-xl border border-gray-600 text-gray-300 text-sm font-bold">
            Cancel
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => onConfirm(luckLevel)}
            className="flex-1 py-2.5 rounded-xl bg-amber-700 text-white text-sm font-bold disabled:opacity-40"
          >
            Craft
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function GemExchangePanel({
  gems,
  config,
  heroClass,
  gold,
  crypto,
  fromType,
  toType,
  level,
  loading,
  onFromChange,
  onToChange,
  onLevelChange,
  onExchange,
}: {
  gems: PlayerGemStack[];
  config: BlacksmithConfig | null;
  heroClass?: string;
  gold: number;
  crypto: number;
  fromType: CraftableType;
  toType: CraftableType;
  level: number;
  loading: boolean;
  onFromChange: (t: CraftableType) => void;
  onToChange: (t: CraftableType) => void;
  onLevelChange: (lv: number) => void;
  onExchange: () => void;
}) {
  const maxLevel = config?.gem_craft?.max_level ?? 12;
  const cryptoCost = config?.gem_craft?.gem_exchange?.crypto_cost ?? 100;
  const goldCost = Number(
    config?.gem_craft?.gem_exchange?.gold_cost_by_level?.[String(level)] ??
      3000 + Math.max(0, level - 1) * 2000,
  );
  const ownedFrom =
    gems.find((g) => g.gem_type === fromType && g.level === level)?.quantity ?? 0;
  const typesMismatch = fromType === toType;
  const canExchange =
    !typesMismatch &&
    ownedFrom >= 1 &&
    gold >= goldCost &&
    crypto >= cryptoCost &&
    !loading;

  return (
    <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-4 space-y-3">
      <div>
        <div className="text-xs text-gray-400 font-bold uppercase tracking-wide">Gem exchange</div>
        <p className="text-[11px] text-gray-500 mt-1">
          Swap one combat gem for another of the same level. Cost: gold + crypto.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="space-y-1">
          <span className="text-[10px] text-gray-500 uppercase">From</span>
          <select
            value={fromType}
            onChange={(e) => onFromChange(e.target.value as CraftableType)}
            className="w-full bg-kingdom-dark border border-kingdom-mid rounded-lg px-2 py-2 text-sm text-white"
          >
            {CRAFTABLE_TYPES.map((t) => (
              <option key={t} value={t}>
                {formatGemLabel(t, heroClass)}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="text-[10px] text-gray-500 uppercase">To</span>
          <select
            value={toType}
            onChange={(e) => onToChange(e.target.value as CraftableType)}
            className="w-full bg-kingdom-dark border border-kingdom-mid rounded-lg px-2 py-2 text-sm text-white"
          >
            {CRAFTABLE_TYPES.map((t) => (
              <option key={t} value={t}>
                {formatGemLabel(t, heroClass)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="block space-y-1">
        <span className="text-[10px] text-gray-500 uppercase">Level</span>
        <input
          type="range"
          min={1}
          max={maxLevel}
          value={level}
          onChange={(e) => onLevelChange(Number(e.target.value))}
          className="w-full"
        />
        <div className="text-xs text-gray-400 text-center">Lv.{level}</div>
      </label>

      <div className="text-xs text-gray-400 space-y-0.5">
        <div>
          Owned {formatGemLabel(fromType, heroClass)} Lv.{level}: {ownedFrom}
        </div>
        <div>Gold: {goldCost.toLocaleString()} (have {gold.toLocaleString()})</div>
        <div>
          Crypto: {cryptoCost} (have {crypto.toLocaleString()})
        </div>
        {typesMismatch && <div className="text-rose-300">Pick a different target gem type.</div>}
      </div>

      <button
        type="button"
        disabled={!canExchange}
        onClick={onExchange}
        className="w-full py-2.5 rounded-xl bg-amber-700 text-white font-bold text-sm disabled:opacity-40"
      >
        {loading ? "Exchanging…" : `Exchange 1× Lv.${level}`}
      </button>
    </div>
  );
}

function GemQuickModal({
  gem,
  heroClass,
  maxSellQty,
  sellQty,
  sellPrice,
  loading,
  onSellQtyChange,
  onClose,
  onSell,
}: {
  gem: GemInstance;
  heroClass?: string;
  maxSellQty: number;
  sellQty: number;
  sellPrice: number;
  loading: boolean;
  onSellQtyChange: (n: number) => void;
  onClose: () => void;
  onSell: () => void;
}) {
  const label = formatGemLabel(gem.gem_type, heroClass);
  return (
    <ModalBackdrop onClose={onClose}>
      <div className="w-full max-w-sm bg-kingdom-purple border border-kingdom-light rounded-2xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className={clsx("font-bold", gemTypeColor(gem.gem_type, heroClass))}>
            {label} Lv.{gem.level}
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 text-xl">✕</button>
        </div>
        <p className="text-xs text-gray-400">Owned: {maxSellQty} · Sell: {(sellPrice * sellQty).toLocaleString()} gold</p>
        {maxSellQty > 1 && (
          <input
            type="range"
            min={1}
            max={maxSellQty}
            value={sellQty}
            onChange={(e) => onSellQtyChange(Number(e.target.value))}
            className="w-full"
          />
        )}
        <button
          type="button"
          disabled={loading || maxSellQty < 1}
          onClick={onSell}
          className="w-full py-2.5 rounded-xl bg-rose-800/80 text-white font-bold text-sm disabled:opacity-40"
        >
          Sell ×{sellQty}
        </button>
      </div>
    </ModalBackdrop>
  );
}
